import React, { useCallback, useState } from 'react';

import { Body } from 'js/design_system/Body';
import { ContinueWithGoogleButton } from 'js/login_modal/components/ContinueWithGoogleButton';
import { ContinueWithAppleButton } from 'js/login_modal/components/ContinueWithAppleButton';
import { ContinueWithEmailButton } from 'js/login_modal/components/ContinueWithEmailButton';
import { Icon } from 'js/design_system/Icon';
import { GateOverlaySocialProof } from './GateOverlaySocialProof';

export type GateOverlayProps = {
  universityCount: number;
  userInChina: boolean;
};

const registrationParams = { user_source: 'download' as const };
const clickTrackingContext = { source: 'loswp-gate-overlay' };

export const GateOverlay: React.FC<GateOverlayProps> = ({
  universityCount,
  userInChina,
}) => {
  const [loading, setLoading] = useState(false);

  const onSuccess = useCallback((redirect?: string) => {
    window.location.href = redirect ?? window.location.href;
  }, []);

  // On OAuth error, open the modal so the user can see what went wrong and retry
  const onError = useCallback(() => {
    setLoading(false);
    document.dispatchEvent(
      new CustomEvent('open-login-modal', {
        detail: {
          postLoginRedirectUrl: window.location.href,
          clickTrackingContext,
          registrationParams,
        },
      }),
    );
  }, []);

  const onEmailClick = useCallback(() => {
    document.dispatchEvent(
      new CustomEvent('open-login-modal', {
        detail: {
          postLoginRedirectUrl: window.location.href,
          initialPage: 'email-entry',
          clickTrackingContext,
          registrationParams,
        },
      }),
    );
  }, []);

  return (
    <div className="gate-overlay">
      <div className="gate-overlay__content">
        <Body as="h2" size="xl" bold className="gate-overlay__title">
          Continue reading for free
        </Body>
        <ul className="gate-overlay__benefits">
          <Body as="li" size="sm" className="gate-overlay__benefit-item">
            <Icon name="check" size={18} />
            Access research papers &amp; books
          </Body>
          <Body as="li" size="sm" className="gate-overlay__benefit-item">
            <Icon name="check" size={18} />
            Unlimited downloads
          </Body>
          <Body as="li" size="sm" className="gate-overlay__benefit-item">
            <Icon name="check" size={18} />
            Related papers
          </Body>
        </ul>
        <div className="gate-overlay__buttons">
          {!userInChina && (
            <ContinueWithGoogleButton
              buttonText="Continue with Google"
              className="gate-overlay__social-btn"
              setLoading={setLoading}
              disabled={loading}
              registrationParams={registrationParams}
              onSuccess={onSuccess}
              onError={onError}
              clickTrackingContext={clickTrackingContext}
            />
          )}
          <ContinueWithAppleButton
            buttonText="Continue with Apple"
            className="gate-overlay__social-btn"
            setLoading={setLoading}
            disabled={loading}
            registrationParams={registrationParams}
            onSuccess={onSuccess}
            onError={onError}
            clickTrackingContext={clickTrackingContext}
          />
          <ContinueWithEmailButton
            buttonText="Or continue with email"
            className="gate-overlay__email-link"
            onClick={onEmailClick}
            userInChina={userInChina}
          />
        </div>
      </div>
      <div className="gate-overlay__social-proof-section">
        <GateOverlaySocialProof
          header={`Used by Academics at ${universityCount.toLocaleString()} universities`}
        />
      </div>
    </div>
  );
};
